import { RECIPE_SITES, recipeApp, type OsRecipe } from '../os-recipes.js';

const SAFARI = recipeApp('com.apple.Safari');

/** safari: its app recipes as data (Mac recipes R3). Every entry ships held until its drill lifts it.
 *  The research rows not here are on the R3 review lists. */
export const RECIPES: readonly OsRecipe[] = [
  {
    id: 'safari.new_tab', app: SAFARI, status: 'held', opens: true,
    title: 'Open a new Safari tab',
    description: 'Open a new Safari tab ("new tab in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+t' }],
    tier: 'reversible', cues: ['naya', 'another', 'new tab', 'new safari tab', 'another tab', 'naya tab'],
    reply: 'Opened a new tab in Safari.',
  },
  // A close loses what the tab held (a half-filled form): the lint keeps safari.close_tab for the yes.
  {
    id: 'safari.close_tab', app: SAFARI, status: 'held',
    title: 'Close the Safari tab',
    description: 'Close the Safari tab ("close this tab in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'verb', id: 'safari.close_tab', params: {} }],
    tier: 'irreversible', verify: 'echo', cues: ['close this tab', 'close the safari tab', 'shut this tab', 'yeh tab band', 'close the current tab'],
    reply: 'Close this tab in Safari?',
  },
  {
    id: 'safari.reopen_tab', app: SAFARI, status: 'held', opens: true,
    title: 'Reopen the last closed Safari tab',
    description: 'Reopen the last closed Safari tab ("reopen the tab I closed").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+shift+t' }],
    tier: 'reversible', cues: ['reopen', 'restore', 'closed', 'wapas', 'tab i closed', 'back the closed tab', 'restore that tab', 'oops reopen it', 'woh tab wapas', 'closed tab'],
    reply: 'Reopened the last closed tab in Safari.',
  },
  {
    id: 'safari.next_tab', app: SAFARI, status: 'held', opens: true,
    title: 'Go to the next Safari tab',
    description: 'Go to the next Safari tab ("next tab").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+tab' }],
    tier: 'reversible', cues: ['next', 'right', 'agla', 'next tab', 'tab on the right', 'agla tab'],
    reply: 'Next tab.',
  },
  {
    id: 'safari.previous_tab', app: SAFARI, status: 'held', opens: true,
    title: 'Go to the previous Safari tab',
    description: 'Go to the previous Safari tab ("previous tab").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+shift+tab' }],
    tier: 'reversible', cues: ['previous', 'prev', 'left', 'pichla', 'previous tab', 'tab on the left', 'pichla tab'],
    reply: 'Previous tab.',
  },
  {
    id: 'safari.pin_tab', app: SAFARI, status: 'held',
    title: 'Pin the Safari tab',
    description: 'Pin the Safari tab ("pin this tab in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Pin Tab'], pair: ['Pin Tab', 'Unpin Tab'] }],
    tier: 'reversible', cues: ['pin', 'pinned', 'pin this tab', 'pin the tab', 'keep this tab pinned', 'pin it', 'is tab ko pin', 'pin the current tab'],
    reply: 'Pinned the tab in Safari.',
  },
  {
    id: 'safari.unpin_tab', app: SAFARI, status: 'held',
    title: 'Unpin the Safari tab',
    description: 'Unpin the Safari tab ("unpin this tab in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Unpin Tab'], pair: ['Unpin Tab', 'Pin Tab'] }],
    tier: 'reversible', cues: ['unpin', 'unpin this tab', 'unpin the tab', 'unpin it', 'unpin the current tab', 'yeh tab unpin', 'unpin the pinned tab'],
    reply: 'Unpinned the tab in Safari.',
  },
  {
    id: 'safari.duplicate_tab', app: SAFARI, status: 'held',
    title: 'Duplicate the Safari tab',
    description: 'Duplicate the Safari tab ("duplicate this tab").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Duplicate Tab'] }],
    tier: 'reversible', cues: ['duplicate', 'clone', 'copy', 'duplicate this tab', 'clone this tab', 'copy of this tab', 'duplicate the current tab', 'duplicate tab'],
    reply: 'Duplicated the tab in Safari.',
  },
  {
    id: 'safari.move_tab_to_new_window', app: SAFARI, status: 'held',
    title: 'Move the Safari tab to a new window',
    description: 'Move the Safari tab to a new window ("move this tab to a new window").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Move Tab to New Window'] }],
    tier: 'reversible', cues: ['move', 'separate', 'alag'],
    reply: 'Moved the tab to a new Safari window.',
  },
  {
    id: 'safari.merge_windows', app: SAFARI, status: 'held',
    title: 'Merge all Safari windows',
    description: 'Merge all Safari windows ("merge all windows").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Merge All Windows'] }],
    tier: 'reversible', cues: ['merge', 'combine', 'together', 'merge all windows', 'merge windows', 'saari windows merge'],
    reply: 'Merged your Safari windows into one.',
  },
  {
    id: 'safari.mute_tab', app: SAFARI, status: 'held',
    title: 'Mute the Safari tab',
    description: 'Mute the Safari tab ("mute this tab").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['Window', 'Mute Current Tab'], pair: ['Mute Current Tab', 'Unmute Current Tab'] }],
    tier: 'reversible', cues: ['mute', 'silence', 'awaaz', 'mute this tab', 'mute the tab', 'silence this tab', 'mute the noisy tab', 'mute tab'],
    reply: 'Muted this tab in Safari.',
  },
  {
    id: 'safari.new_window', app: SAFARI, status: 'held', opens: true,
    title: 'Open a new Safari window',
    description: 'Open a new Safari window ("new safari window").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.new_window', params: {} }],
    tier: 'reversible', verify: 'echo', cues: ['nayi', 'another', 'fresh', 'new window', 'new safari window', 'another window', 'another safari window', 'fresh window', 'nayi window'],
    reply: 'Opened a new Safari window.',
  },
  {
    id: 'safari.close_window', app: SAFARI, status: 'held',
    title: 'Close the front Safari window',
    description: 'Close the front Safari window ("close this safari window").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'verb', id: 'safari.close_window', params: {} }],
    tier: 'irreversible', verify: 'echo', cues: ['close this safari window', 'close the window', 'shut the whole window', 'yeh window band'],
    reply: 'Close the front Safari window and all its tabs?',
  },
  {
    id: 'safari.reload', app: SAFARI, status: 'held',
    title: 'Reload the Safari page',
    description: 'Reload the Safari page ("reload").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.reload', params: {} }],
    tier: 'reversible', verify: 'echo', cues: ['reload', 'refresh', 'reload this', 'refresh the page', 'refresh this tab', 'page refresh', 'refresh it'],
    reply: 'Reloaded the page in Safari.',
  },
  {
    id: 'safari.go_to', app: SAFARI, status: 'held', opens: true,
    title: 'Open a well-known site in Safari',
    description: 'Open a well-known site in Safari ("open YouTube in safari").',
    params: { site: { kind: 'enum', description: 'The site to open', enum: Object.values(RECIPE_SITES), labels: Object.fromEntries(Object.entries(RECIPE_SITES).map(([n, u]) => [u, n])) } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: '{site}' } }],
    tier: 'reversible', verify: 'echo', cues: ['go', 'open', 'switch', 'jump', 'take', 'show', 'pull', 'bring', 'visit', 'load', 'kholo'],
    reply: 'Opened {label} in Safari.',
  },
  {
    id: 'safari.search', app: SAFARI, status: 'held', opens: true,
    title: 'Search the web in Safari',
    description: 'Search the web in Safari ("search safari for train times").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.search', params: { query: '{query}' } }],
    tier: 'reversible', verify: 'echo', cues: ['search', 'look', 'google', 'search the web'],
    reply: 'Searched the web for {query} in Safari.',
  },
  {
    id: 'safari.search_youtube', app: SAFARI, status: 'held', opens: true,
    title: 'Search YouTube in Safari',
    description: 'Search YouTube in Safari ("search YouTube for lofi in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://www.youtube.com/results?search_query={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['youtube'], cues: ['search', 'look', 'find'],
    reply: 'Searched YouTube for {query} in Safari.',
  },
  {
    id: 'safari.search_google_images', app: SAFARI, status: 'held', opens: true,
    title: 'Search Google Images in Safari',
    description: 'Search Google Images in Safari ("search google images for blue sofas in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://www.google.com/search?udm=2&q={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['google images'], cues: ['search', 'look', 'find'],
    reply: 'Searched Google Images for {query} in Safari.',
  },
  {
    id: 'safari.search_github', app: SAFARI, status: 'held', opens: true,
    title: 'Search GitHub in Safari',
    description: 'Search GitHub in Safari ("look up react on github in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://github.com/search?q={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['github'], cues: ['search', 'look', 'find'],
    reply: 'Searched GitHub for {query} in Safari.',
  },
  {
    id: 'safari.search_reddit', app: SAFARI, status: 'held', opens: true,
    title: 'Search Reddit in Safari',
    description: 'Search Reddit in Safari ("search reddit for macbook tips in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://www.reddit.com/search/?q={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['reddit'], cues: ['search', 'look', 'find'],
    reply: 'Searched Reddit for {query} in Safari.',
  },
  {
    id: 'safari.search_x', app: SAFARI, status: 'held', opens: true,
    title: 'Search X in Safari',
    description: 'Search X in Safari ("search twitter for monsoon updates in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://x.com/search?q={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['twitter'], cues: ['search', 'look', 'find'],
    reply: 'Searched X for {query} in Safari.',
  },
  {
    id: 'safari.search_linkedin', app: SAFARI, status: 'held', opens: true,
    title: 'Search LinkedIn in Safari',
    description: 'Search LinkedIn in Safari ("search linkedin for data engineers in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://www.linkedin.com/search/results/all/?keywords={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['linkedin'], cues: ['search', 'look', 'find'],
    reply: 'Searched LinkedIn for {query} in Safari.',
  },
  {
    id: 'safari.find_in_page', app: SAFARI, status: 'held', opens: true,
    title: 'Find words on the Safari page',
    description: 'Find words on the Safari page ("find pricing on this page").',
    params: { text: { kind: 'literal', description: 'The exact words to find', cut: ['query', 'after_saying'] } },
    steps: [{ op: 'activate' }, { op: 'focus_mark' }, { op: 'key', chord: 'cmd+f' }, { op: 'focus_moved', timeoutMs: 800, labelIn: ['find', 'search'] }, { op: 'type', param: 'text' }],
    tier: 'reversible', cues: ['find', 'page', 'where', 'dhundo', 'search this page', 'this page', 'is page'],
    reply: 'Looking for {text} on the page.',
  },
  {
    id: 'safari.find_next', app: SAFARI, status: 'held', opens: true,
    title: 'Next match on the Safari page',
    description: 'Next match on the Safari page ("next match").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+g' }],
    tier: 'reversible', cues: ['match', 'result', 'occurrence', 'next match', 'next occurrence', 'agla match'],
    reply: 'Next match.',
  },
  {
    id: 'safari.zoom_in', app: SAFARI, status: 'held',
    title: 'Zoom in on the Safari page',
    description: 'Zoom in on the Safari page ("zoom in").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+=' }],
    tier: 'reversible', cues: ['zoom', 'bigger', 'larger', 'enlarge', 'bada', 'zoom in on', 'it bigger', 'page larger'],
    reply: 'Zoomed in.',
  },
  {
    id: 'safari.zoom_out', app: SAFARI, status: 'held',
    title: 'Zoom out on the Safari page',
    description: 'Zoom out on the Safari page ("zoom out").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+-' }],
    tier: 'reversible', cues: ['zoom', 'smaller', 'shrink', 'chhota', 'zoom out on', 'it smaller', 'shrink the page'],
    reply: 'Zoomed out.',
  },
  {
    id: 'safari.zoom_reset', app: SAFARI, status: 'held',
    title: 'Reset the Safari page zoom',
    description: 'Reset the Safari page zoom ("actual size").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+0' }],
    tier: 'reversible', cues: ['zoom', 'actual', 'normal', 'reset', 'actual size', 'zoom in', 'back to normal size', 'undo the zoom', 'normal size', 'zoom to'],
    reply: 'Reset the zoom.',
  },
  {
    id: 'safari.reader_on', app: SAFARI, status: 'held', opens: true,
    title: 'Show Safari Reader',
    description: 'Show Safari Reader ("reader mode").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Show Reader'], pair: ['Show Reader', 'Hide Reader'] }],
    tier: 'reversible', cues: ['reader', 'easier to read', 'reader mode', 'reader view', 'this in reader', 'turn on reader'],
    reply: 'Showing this page in Reader.',
  },
  {
    id: 'safari.reader_off', app: SAFARI, status: 'held',
    title: 'Hide Safari Reader',
    description: 'Hide Safari Reader ("exit reader").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Hide Reader'], pair: ['Hide Reader', 'Show Reader'] }],
    tier: 'reversible', cues: ['exit', 'leave', 'off', 'hide', 'close', 'exit reader', 'leave reader', 'hide reader', 'reader off', 'close reader view', 'turn off reader', 'turn off reader mode'],
    reply: 'Left Reader.',
  },
  {
    id: 'safari.enter_full_screen', app: SAFARI, status: 'held',
    title: 'Put Safari in full screen',
    description: 'Safari full screen on ("full screen").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Enter Full Screen'], pair: ['Enter Full Screen', 'Exit Full Screen'] }],
    tier: 'reversible', cues: ['full screen', 'fullscreen'],
    reply: 'Safari is full screen.',
  },
  {
    id: 'safari.exit_full_screen', app: SAFARI, status: 'held',
    title: 'Take Safari out of full screen',
    description: 'Safari full screen off ("exit full screen").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Exit Full Screen'], pair: ['Exit Full Screen', 'Enter Full Screen'] }],
    tier: 'reversible', cues: ['exit', 'leave', 'hatao', 'out', 'exit full screen', 'exit fullscreen', 'leave fullscreen', 'leave full screen', 'full screen hatao', 'out of full screen'],
    reply: 'Safari is out of full screen.',
  },
  {
    id: 'safari.focus_address_bar', app: SAFARI, status: 'held',
    title: "Put the cursor in Safari's address field",
    description: 'Put the cursor in Safari\'s address field ("go to the address bar in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'focus_mark' }, { op: 'key', chord: 'cmd+l' }, { op: 'focus_moved', timeoutMs: 800, labelIn: ['address', 'search', 'smart search'] }],
    tier: 'reversible', cues: ['address', 'url', 'location', 'search field', 'address bar', 'focus the url bar', 'select the search field', 'address bar pe jao'],
    reply: 'The address field is ready.',
  },
  {
    id: 'safari.show_bookmarks', app: SAFARI, status: 'held', opens: true,
    title: 'Show Safari bookmarks',
    description: 'Show Safari bookmarks ("show my bookmarks in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.show_bookmarks', params: {} }],
    tier: 'reversible', verify: 'echo', cues: ['bookmarks', 'bookmarks dikhao'],
    reply: 'Showing your bookmarks in Safari.',
  },
  {
    id: 'safari.show_favorites_bar', app: SAFARI, status: 'held', opens: true,
    title: "Show Safari's favorites bar",
    description: 'Show Safari\'s favorites bar ("show the favorites bar").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Show Favorites Bar'], pair: ['Show Favorites Bar', 'Hide Favorites Bar'] }],
    tier: 'reversible', cues: ['favorites bar', 'favourites bar', 'bookmarks bar', 'favorites bar dikhao'],
    reply: 'The favorites bar is showing.',
  },
  {
    id: 'safari.hide_favorites_bar', app: SAFARI, status: 'held',
    title: "Hide Safari's favorites bar",
    description: 'Hide Safari\'s favorites bar ("hide the favorites bar").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Hide Favorites Bar'], pair: ['Hide Favorites Bar', 'Show Favorites Bar'] }],
    tier: 'reversible', cues: ['hide', 'chhupao', 'hatao', 'hide the favorites bar', 'hide the favourites bar', 'hide the bookmarks bar', 'favorites bar hatao', 'bookmarks bar hatao'],
    reply: 'The favorites bar is hidden.',
  },
  {
    id: 'safari.show_reading_list', app: SAFARI, status: 'held', opens: true,
    title: "Show Safari's Reading List",
    description: 'Show Safari\'s Reading List ("show my reading list").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Show Reading List Sidebar'], pair: ['Show Reading List Sidebar', 'Hide Reading List Sidebar'] }],
    tier: 'reversible', cues: ['reading list', 'later', 'reading list sidebar'],
    reply: 'Showing your Reading List.',
  },
  {
    id: 'safari.show_sidebar', app: SAFARI, status: 'held', opens: true,
    title: 'Show the Safari sidebar',
    description: 'Show the Safari sidebar ("show the sidebar in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Show Sidebar'], pair: ['Show Sidebar', 'Hide Sidebar'] }],
    tier: 'reversible', cues: ['sidebar', 'tab groups', 'sidebar dikhao'],
    reply: 'The sidebar is showing.',
  },
  {
    id: 'safari.hide_sidebar', app: SAFARI, status: 'held',
    title: 'Hide the Safari sidebar',
    description: 'Hide the Safari sidebar ("hide the sidebar in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Hide Sidebar'], pair: ['Hide Sidebar', 'Show Sidebar'] }],
    tier: 'reversible', cues: ['hide', 'chhupao', 'hatao', 'hide the sidebar', 'hide sidebar', 'sidebar hatao', 'hide the safari sidebar'],
    reply: 'The sidebar is hidden.',
  },
  {
    id: 'safari.settings', app: SAFARI, status: 'held', opens: true,
    title: 'Open Safari settings',
    description: 'Open Safari settings ("open safari settings").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+,' }],
    tier: 'reversible', cues: ['settings', 'preferences'],
    reply: 'Opened Safari settings.',
  },
  // Sharing is outward: the yes only opens the share sheet, and the person picks who it goes to.
  {
    id: 'safari.share', app: SAFARI, status: 'held',
    title: "Open Safari's share menu",
    description: 'Open Safari\'s share menu ("share this page").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'menu', path: ['File', 'Share…'] }],
    tier: 'irreversible', yesWord: 'share', cues: ['share', 'airdrop', 'share this page', 'share this', 'airdrop this page', 'yeh page share', 'share this link'],
    reply: 'Share this page from Safari?',
  },
  // safari.email_contents is a sensitive verb (the whole page goes into Mail), so it waits for the yes; nothing is sent.
  {
    id: 'safari.email_page', app: SAFARI, status: 'held',
    title: 'Put the Safari page in a new email',
    description: 'Put the Safari page in a new email ("email this page").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'verb', id: 'safari.email_contents', params: {} }],
    tier: 'irreversible', verify: 'echo', cues: ['email', 'mail', 'email this page', 'yeh page email', 'email this article'],
    reply: 'Put this page in a new email in Mail?',
  },
  // Printing is outward (the catalog audit leaves prints for the yes); the yes only opens the dialog.
  {
    id: 'safari.print', app: SAFARI, status: 'held',
    title: 'Open print for the Safari page',
    description: 'Open print for the Safari page ("print this page").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'key', chord: 'cmd+p' }],
    tier: 'irreversible', cues: ['print', 'print this page', 'print this'],
    reply: 'Open the print window for this page in Safari?',
  },  // ── R3c: the catalog rulings' accepts ─────────────────────────────────────────────────────────
  // Menu presses never reach the page, where cmd+. is superscript in Google Docs; stopping a load drops no call.
  {
    id: 'safari.stop_loading', app: SAFARI, status: 'held',
    title: 'Stop the Safari page loading',
    description: 'Stop the Safari page loading ("stop loading").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Stop'] }],
    tier: 'reversible', cues: ['stop', 'cancel', 'stop loading', 'stop the page', 'stop loading this page', 'cancel the page load', 'page loading stop'],
    reply: 'Stopped loading the page.',
  },
  {
    id: 'safari.downloads', app: SAFARI, status: 'held', opens: true,
    title: 'Show Safari downloads',
    description: 'Show Safari downloads ("show my downloads in safari").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Show Downloads'] }],
    tier: 'reversible', cues: ['downloads', 'download', 'safari downloads', 'downloads dikhao'],
    reply: 'Showing your downloads in Safari.',
  },
  // Wikipedia's search page lists results (fulltext=1), so a title match never opens a page on its own.
  {
    id: 'safari.search_wikipedia', app: SAFARI, status: 'held', opens: true,
    title: 'Search Wikipedia in Safari',
    description: 'Search Wikipedia in Safari ("search wikipedia for chola dynasty in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://en.wikipedia.org/w/index.php?fulltext=1&search={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['wikipedia'], cues: ['search', 'look', 'find'],
    reply: 'Searched Wikipedia for {query} in Safari.',
  },
  // Google's map search. "maps" is also the Maps app's name, so today these asks are the model's.
  {
    id: 'safari.search_google_maps', app: SAFARI, status: 'held', opens: true,
    title: 'Search Google Maps in Safari',
    description: 'Search Google Maps in Safari ("find coffee on google maps in safari").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'safari.open_tab', params: { url: 'https://www.google.com/maps/search/{query}' } }],
    tier: 'reversible', verify: 'echo', names: ['google maps'], cues: ['search', 'look', 'find'],
    reply: 'Searched Google Maps for {query} in Safari.',
  },
];
